export type SimulationDurationMode = 'until_stopped' | 'fixed';

export type SimulationRunMode = 'calculate' | 'realtime';

export const DURATION_PRESETS_MINUTES = [
  15, 30, 60, 120, 240, 480, 720, 1440,
] as const;

/** Max fixed run length (7 days). */
export const MAX_DURATION_MINUTES = 10_080;

export const MIN_VISUAL_FPS = 1;
export const MAX_VISUAL_FPS = 25;
export const DEFAULT_VISUAL_FPS = 10;

export const VISUAL_FPS_PRESETS = [1, 5, 10, 15, 20, 25] as const;

/** @deprecated Use visualFps — kept for migration from stored ms values. */
export const DEFAULT_VISUAL_REFRESH_MS = 1000 / DEFAULT_VISUAL_FPS;
export const MIN_VISUAL_REFRESH_MS = 1000 / MAX_VISUAL_FPS;

export function visualFpsToIntervalMs(fps: number): number {
  const clamped = Math.min(MAX_VISUAL_FPS, Math.max(MIN_VISUAL_FPS, Math.round(fps)));
  return Math.round(1000 / clamped);
}

export function visualIntervalMsToFps(ms: number): number {
  if (ms <= 0) return DEFAULT_VISUAL_FPS;
  return Math.min(
    MAX_VISUAL_FPS,
    Math.max(MIN_VISUAL_FPS, Math.round(1000 / ms)),
  );
}

export function formatVisualFpsLabel(fps: number): string {
  const drawIntervalMs = visualFpsToIntervalMs(fps);
  return `${fps} FPS · ${drawIntervalMs} ms snapshot interval`;
}

export function formatDurationMinutes(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  if (remainder === 0) {
    return hours === 1 ? '1 hour' : `${hours} hours`;
  }
  return `${hours}h ${remainder}m`;
}

export function formatSimTimeSeconds(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const minutes = Math.floor(seconds / 60);
  const remainder = Math.round(seconds % 60);
  if (minutes < 60) {
    return remainder > 0 ? `${minutes}m ${remainder}s` : `${minutes}m`;
  }
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
}

/** Live simulation clock for playback controls (m:ss or h:mm:ss). */
export function formatSimulationClock(seconds: number): string {
  const total = Math.max(0, Math.floor(seconds));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const ss = String(s).padStart(2, '0');
  if (h > 0) {
    return `${h}:${String(m).padStart(2, '0')}:${ss}`;
  }
  return `${m}:${ss}`;
}

export const SIMULATION_TIME_SCALE_PRESETS = [1, 2, 5] as const;
export type SimulationTimeScale = (typeof SIMULATION_TIME_SCALE_PRESETS)[number];
